// Status-only helpers for places that list or confirm posts (Manage posts
// rows, the composer's post-publish message). The wording comes from
// moderationStatusView, the same view the composer's status panel shows.
// Nothing here reads findings.
import { POLL_INTERVALS } from '../../lib/constants'
import { moderationStatusView } from './moderationReviewModel'
import { checkFailureClass } from './moderationWords'

const BADGE_TONES = { error: 'danger', warning: 'warning', info: 'info', default: 'neutral' }

// isModerationHeld is true while moderation holds or rejected the post's
// current revision, or, while it enforces the post, a check of it failed. A
// hold outlives the organization's switch: a post held before moderation was
// turned off stays held until it is decided or released. A passed or exempt
// post, and an unenforced post without a hold, is never held.
export function isModerationHeld(moderation) {
  if (!moderation) return false
  const { status, hold_reason: hold } = moderation
  if (status === 'passed' || status === 'exempt') return false
  return Boolean(hold) || status === 'rejected' || Boolean(moderation.enforced && moderation.check_failed)
}

// moderationHoldBadge is the list row chip for a held post, or null. A live
// post moderation never checked is not held but still gets a neutral chip;
// a post marked for deletion gets none (its deletion countdown chip shows).
export function moderationHoldBadge(moderation) {
  if (!moderation?.not_checked && !isModerationHeld(moderation)) return null
  const view = moderationStatusView(moderation)
  if (!view || view.badge === false) return null
  return { key: 'moderation', label: view.title, tone: BADGE_TONES[view.tone] || 'warning' }
}

// moderationPollInterval is how often a view of the post should refetch its
// moderation state: fast while a check runs, slowly while a failed check
// waits on the provider or on Wager, and not at all otherwise.
export function moderationPollInterval(moderation) {
  const view = moderationStatusView(moderation)
  if (!view?.poll) return false
  return view.pollSlow ? POLL_INTERVALS.SLOW : POLL_INTERVALS.FAST
}

// How long a list keeps polling fast while the same rows stay pending. A
// check that has not finished by then is stuck rather than about to clear,
// so the list falls back to the slow interval until the set changes.
export const FAST_ROW_POLL_LIMIT_MS = 2 * 60 * 1000

// rowPollSpeed says how soon a row's moderation badge could change on its
// own: 'fast' while one of its checks is queued or running, 'slow' while it
// waits on the provider or on Wager (an outage hold, a check that failed on
// the provider, an incomplete check), null otherwise. A check that failed
// on the post itself (its media, its content or its regulations, the
// row's failure_code) fails the same way until someone changes the post or
// its regulations, and a refused provider result is never retried on its
// own, so those rows do not poll. A row without a failure_code (an older
// server) reads as an outage and keeps polling slowly.
function rowPollSpeed(moderation) {
  if (!isModerationHeld(moderation)) return null
  if (moderation.check_failed && checkFailureClass(moderation.failure_code) !== 'outage') return null
  const view = moderationStatusView(moderation)
  if (!view?.poll) return null
  const hold = moderation.hold_reason
  return !view.pollSlow && (hold === 'provider_pending' || hold === 'asset_pending') ? 'fast' : 'slow'
}

// moderationRowsPollInterval is how often a list refetches while a visible
// row's moderation badge could change on its own. rows is [{ key,
// moderation }]; tracker is the tracker the previous call returned (null at
// first). It returns { interval, tracker }: FAST while some row is still
// being checked, falling back to SLOW once the same set of checked rows has
// been polled fast for FAST_ROW_POLL_LIMIT_MS (a new set starts the clock
// again); SLOW while rows only wait on the provider; false when no badge
// would change.
export function moderationRowsPollInterval(rows = [], tracker = null, now = Date.now()) {
  const fastKeys = []
  let slow = false
  for (const row of rows || []) {
    const speed = rowPollSpeed(row?.moderation)
    if (speed === 'fast') fastKeys.push(String(row.key))
    else if (speed === 'slow') slow = true
  }
  if (!fastKeys.length) {
    return { interval: slow ? POLL_INTERVALS.SLOW : false, tracker: null }
  }
  const pendingKey = fastKeys.sort().join('\n')
  const fastSince = tracker?.pendingKey === pendingKey && Number.isFinite(tracker.fastSince) ? tracker.fastSince : now
  return {
    interval: now - fastSince >= FAST_ROW_POLL_LIMIT_MS ? POLL_INTERVALS.SLOW : POLL_INTERVALS.FAST,
    tracker: { pendingKey, fastSince },
  }
}

// A check still running gets this notice: it never promises when, or
// whether, the post goes live.
const GENERIC_HELD_NOTICE = {
  tone: 'info',
  message: 'Post saved, not live yet. Moderation is checking it; the result shows on the post and in Manage posts.',
}

// moderationPublishNotice reads a create response and returns what the
// composer says after saving a post that moderation is holding, or null when
// moderation is not holding it. moderation_held without a read model (the
// read failed) still gets a message that never claims the post is live. lead
// names what was done ("Generated parlay approved"); it defaults to a save.
export function moderationPublishNotice(response, lead = 'Post saved') {
  const moderation = response?.moderation
  const checking = { tone: GENERIC_HELD_NOTICE.tone, message: `${lead}, not live yet. Moderation is checking it; the result shows on the post and in Manage posts.` }
  if (isModerationHeld(moderation)) {
    const view = moderationStatusView(moderation)
    if (view.poll && view.tone === 'info') return checking
    return { tone: view.tone, message: `${lead}, not live yet. ${view.title}. ${view.description}` }
  }
  return response?.moderation_held ? checking : null
}

// A save of an existing post that moderation is still checking: the
// earlier version stays live meanwhile when it was live.
const EDIT_CHECKING_LIVE_NOTICE = {
  tone: 'info',
  message: 'Changes saved, not live yet. Moderation is checking them; the earlier version stays live meanwhile.',
}
const EDIT_HELD_FALLBACK_NOTICE = {
  tone: 'info',
  message: 'Changes saved, not live yet. Moderation is checking them; the result shows on the post and in Manage posts.',
}

// moderationEditNotice reads an update response and returns what the
// editor or the inspector says after saving changes moderation holds, or
// null when moderation is not holding the post.
export function moderationEditNotice(response) {
  const moderation = response?.moderation
  if (isModerationHeld(moderation)) {
    const view = moderationStatusView(moderation)
    if (view.poll && view.tone === 'info') {
      return response?.is_published && !moderation.hidden ? { ...EDIT_CHECKING_LIVE_NOTICE } : { ...GENERIC_HELD_NOTICE }
    }
    return { tone: view.tone, message: `Changes saved, not live. ${view.title}. ${view.description}` }
  }
  return response?.moderation_held ? { ...EDIT_HELD_FALLBACK_NOTICE } : null
}
